import { Progress } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { Button } from '@itsrever/design-system'
import { useCart } from '@/application/use-cart/use-cart'
import { useCredit } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'

interface CreditFooterProps {
    onContinue: () => void
}

export const CreditFooter = ({ onContinue }: CreditFooterProps) => {
    const {
        t,
        i18n: { language }
    } = useTranslation()

    const currency = useAppSelector(
        (store) => store.returnsApi.order.response.order?.money_format?.currency
    )

    const { totalAvailableCredit, spentCredit } = useCredit()

    const { cartItems, getCartTotal } = useCart()

    const cartTotalQuantity = getCartTotal(cartItems)

    const remainingCredit = totalAvailableCredit - spentCredit
    const creditPercentage = (spentCredit * 100) / totalAvailableCredit

    return (
        <div className="flex w-fit items-center justify-center gap-8 rounded-t-2xl border-x border-t border-solid border-neutral-200 bg-white px-6 py-4 shadow-lg">
            {remainingCredit <= 0 ? null : (
                <div className="space-y-2.5">
                    <p className="flex items-center gap-2">
                        <span className="rounded-full bg-radial-gradient from-brand-600 to-brand-500 px-2 py-0.5 font-medium text-white">
                            {remainingCredit.toLocaleString(language, {
                                style: 'currency',
                                currency,
                                minimumFractionDigits:
                                    remainingCredit % 1 === 0 ? 0 : 2,
                                maximumFractionDigits: 2
                            })}
                        </span>
                        <span className="text-sm font-light text-neutral-600">
                            {t('full_catalog.to_spend_from_your_return')}
                        </span>
                    </p>
                    <Progress
                        aria-label="Spent credit"
                        value={creditPercentage}
                        classNames={{
                            base: 'h-2',
                            track: 'bg-brand-200',
                            indicator: 'bg-brand-500'
                        }}
                    />
                </div>
            )}
            {cartTotalQuantity > 0 ? (
                <div className="hidden md:block">
                    <Button onClick={onContinue} id="fc-continue-button">
                        <span className="mx-6 flex items-center justify-center gap-3 text-base">
                            {t('full_catalog.confirm')}
                            <span className="flex size-6 items-center justify-center rounded-full border border-solid border-white/50 text-center text-xs font-semibold">
                                {cartTotalQuantity}
                            </span>
                        </span>
                    </Button>
                </div>
            ) : null}
        </div>
    )
}
